import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Format, Post } from '@/hooks/useCampaigns';
import { format } from 'date-fns';
import { EventBadge } from './EventBadge';

type PostDialogProps = {
  post: Post;
  isOpen: boolean;
  onClose: () => void;
};

const VideoContent = ({ post }: { post: Post }) => (
  <>
    {post.hook && (
      <div className="space-y-2">
        <h3 className="text-sm font-medium">Opening Hook</h3>
        <p className="text-sm text-muted-foreground">{post.hook}</p>
      </div>
    )}

    {post?.brollShots && post.brollShots?.length > 0 && (
      <div className="space-y-2">
        <h3 className="text-sm font-medium">Shot List</h3>
        <p className="text-sm text-muted-foreground">
          {post.brollShots.join(' • ')}
        </p>
      </div>
    )}

    {post.overlayText && (
      <div className="space-y-2">
        <h3 className="text-sm font-medium">Overlay Text</h3>
        <p className="text-sm text-muted-foreground">{post.overlayText}</p>
      </div>
    )}

    {post?.talkingPoints && post.talkingPoints?.length > 0 && (
      <div className="space-y-2">
        <h3 className="text-sm font-medium">Key Talking Points</h3>
        <p className="text-sm text-muted-foreground">
          {post.talkingPoints.join(' • ')}
        </p>
      </div>
    )}
  </>
);

const ImageContent = ({ post }: { post: Post }) => (
  <>
    {post.visualStyle && (
      <div className="space-y-2">
        <h3 className="text-sm font-medium">Visual Direction</h3>
        <p className="text-sm text-muted-foreground">{post.visualStyle}</p>
      </div>
    )}

    {post.photoCopy && (
      <div className="space-y-2">
        <h3 className="text-sm font-medium">Photo Copy</h3>
        <p className="text-sm text-muted-foreground">{post.photoCopy}</p>
      </div>
    )}

    {post.overlayText && (
      <div className="space-y-2">
        <h3 className="text-sm font-medium">Overlay Text</h3>
        <p className="text-sm text-muted-foreground">{post.overlayText}</p>
      </div>
    )}
  </>
);

const CarouselContent = ({ post }: { post: Post }) => (
  <>
    {post.theme && (
      <div className="space-y-2">
        <h3 className="text-sm font-medium">Theme or Storyline</h3>
        <p className="text-sm text-muted-foreground">{post.theme}</p>
      </div>
    )}

    {post.slideFlow && post.slideFlow.length > 0 && (
      <div className="space-y-2">
        <h3 className="text-sm font-medium">{`Slide Flow (${post.slideCount} Slides)`}</h3>
        {post.slideFlow.map((slide, index) => (
          <p className="text-sm text-muted-foreground">{slide}</p>
        ))}
      </div>
    )}

    {post.callToAction && (
      <div className="space-y-2">
        <h3 className="text-sm font-medium">Call-to-Action</h3>
        <p className="text-sm text-muted-foreground">{post.callToAction}</p>
      </div>
    )}
  </>
);

export const PostDialog = ({ post, isOpen, onClose }: PostDialogProps) => {
  const renderContent = () => {
    switch (post.format) {
      case Format.VIDEO:
        return <VideoContent post={post} />;
      case Format.IMAGE:
        return <ImageContent post={post} />;
      case Format.CAROUSEL_ALBUM:
        return <CarouselContent post={post} />;
      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <EventBadge format={post.format} />
            <span className="text-sm text-muted-foreground">
              {format(new Date(post.dateTime), 'PPp')}
            </span>
          </div>

          {renderContent()}

          {post.caption && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium">Caption</h3>
              <p className="text-sm text-muted-foreground">{post.caption}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
